import { isOwnPoint, type WorkflowDocument } from '@luzula/luzula-lib';
import { idOf, istPunktartMitProtokoll } from 'src/app/data-model';

/**
 * „Letzte Nummern" im Reiter Aufnahme: eine Gedaechtnisstuetze, welche Nummer
 * man selbst zuletzt vergeben hat.
 *
 * Es gibt EINE Punkt-Datenbank fuer die ganze App, nicht je Projekt. Die Liste
 * zog bis 15.09.2026 alle Punkte daraus heran, egal aus welchem Projekt und
 * von wem - also die Nummernvergabe aller Leute in allen Projekten (Nutzer:
 * „da stehen nur willkuerliche Nummern drin"). Jetzt: nur eigene Punkte im
 * aktiven Projekt. „Eigen" heisst dasselbe wie beim Bearbeiten (`isOwnPoint`:
 * der letzte Eintrag im Bearbeiter-Verlauf, sonst der Anleger; Altbestand
 * ohne Vermerk zaehlt mit).
 */

/** Eine Zeile des Index (View `application/index`), soweit die Liste sie braucht. */
export interface IndexZeile {
	id: string;
	/** [nummer, type, x, y, kopf, state, version, mesoklima, stfg, workflow, rotation] */
	key: any[];
}

export interface LetzteNummer {
	nummer: string;
	/** Aufnahmedatum, wie am Punkt gespeichert; leer, wenn keins da ist */
	datum: string;
}

export interface LetzteNummernKontext {
	/** Der Punkt, dessen Nummer gerade eingegeben wird - er selbst steht nicht in der Liste. */
	eigenePunktId: string | undefined;
	/** Punktart des Punkts: Moor-Nummern laufen getrennt von den uebrigen. */
	punktart: string;
	/** Aktives Projekt; ohne Projekt gibt es keine Liste. */
	projektId: string;
	/** Angemeldeter Nutzer; ohne Anmeldung gibt es keine Liste. */
	nutzerId: string | undefined;
}

/** Mehr braucht niemand zum Erinnern; die Liste ist keine Punktliste. */
export const LETZTE_NUMMERN_HOECHSTENS = 20;

const MOORPUNKT = 7;

export function letzteNummernAusIndex(rows: IndexZeile[] | undefined, k: LetzteNummernKontext): LetzteNummer[] {
	if (!rows?.length || !k.projektId || !k.nutzerId) return [];
	const moor = istMoor(k.punktart);
	const eintraege: LetzteNummer[] = [];

	for (const row of rows) {
		const nummer = row.key?.[0];
		if (typeof nummer !== 'string' || !nummer.trim()) continue;
		if (row.id === k.eigenePunktId) continue;

		// Nur Langprotokoll-Punkte (Kurzprotokoll bekommt keine Nummer in dieser Reihe),
		// Moor-Nummern getrennt von den uebrigen.
		const type = row.key[1];
		if (!istPunktartMitProtokoll(type) || istMoor(type) !== moor) continue;

		const kopf = row.key[4];
		if (!imProjekt(kopf?.projects, k.projektId)) continue;

		const punkt: WorkflowDocument = { _id: row.id, workflow: row.key[9] ?? undefined };
		if (!isOwnPoint(punkt, k.nutzerId)) continue;

		eintraege.push({ nummer, datum: kopf?.aufnahme?.datum || '' });
	}

	eintraege.sort(neuesteZuerst);
	return eintraege.slice(0, LETZTE_NUMMERN_HOECHSTENS);
}

function istMoor(punktart: string): boolean {
	return idOf(punktart) === MOORPUNKT;
}

function imProjekt(projects: unknown, projektId: string): boolean {
	return Array.isArray(projects) && projects.some(p => p?.id === projektId);
}

/**
 * Der Tag der Aufnahme als Zahl, oder `undefined`, wenn sich kein Datum
 * herauslesen laesst.
 *
 * Das Datum steht als TEXT am Punkt, und zwar in zwei Formaten: frisch
 * angelegt als ISO-Zeitstempel („2026-09-16T08:30:00.000Z"), nach dem
 * Anfassen des Datumsfeldes als UTC-Text mit Wochentag („Fri, 04 Sep 2026
 * 00:00:00 GMT" - `wd-date-input` schreibt `dateUTCString()`). Zeichenweise
 * verglichen entscheidet damit der Wochentagsname ueber die Reihenfolge, und
 * ein ISO-Datum verliert gegen jeden UTC-Text, weil Ziffern vor Buchstaben
 * kommen. Also erst umrechnen, dann vergleichen.
 *
 * Gerechnet wird auf den LOKALEN Tag - denselben, den die Liste daneben
 * anzeigt. Sonst stuenden zwei Punkte mit sichtbar gleichem Datum in
 * verschiedenen Tagesgruppen.
 */
function tagVon(datum: string): number | undefined {
	if (!datum) return undefined;
	const d = new Date(datum);
	if (Number.isNaN(d.getTime())) return undefined;
	return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** Neueste Aufnahme zuerst (ohne Datum ganz hinten); am selben Tag die hoechste Nummer zuerst. */
function neuesteZuerst(a: LetzteNummer, b: LetzteNummer): number {
	const tagA = tagVon(a.datum);
	const tagB = tagVon(b.datum);
	if (tagA !== tagB) {
		if (tagA === undefined) return 1;
		if (tagB === undefined) return -1;
		return tagB - tagA;
	}
	return b.nummer.localeCompare(a.nummer, undefined, { numeric: true });
}
